import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import deLocaleAngular from '@angular/common/locales/de';
import elLocaleAngular from '@angular/common/locales/el';
import enAuLocaleAngular from '@angular/common/locales/en-AU';
import esLocaleAngular from '@angular/common/locales/es';
import esMxLocaleAngular from '@angular/common/locales/es-MX';
import frLocaleAngular from '@angular/common/locales/fr';
import nlLocaleAngular from '@angular/common/locales/nl';
import plLocaleAngular from '@angular/common/locales/pl';
import zhHansLocaleAngular from '@angular/common/locales/zh-Hans';
import zhHantLocaleAngular from '@angular/common/locales/zh-Hant';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import {
  APP_AVAILABLE_LANGUAGES,
  APP_CUSTOM_MODULES,
  APP_LOCALE_DATA,
  CoreModule,
  ENVIRONMENT,
  Environment,
  InterceptorsModule,
  ModalModule,
  ModuleTranslateLoaderFactory,
  TranslateCachedService,
  TranslateLoaderReadyService
} from '@cfp/core';
import { BuildingLayout } from '@cfp/domains/building/layout';
import { PortfolioLayout } from '@cfp/domains/portfolio/layout';
import { NabersApiUnavailableInterceptor } from '@cfp/features-shared/nabers';
import { AuthenticationModule } from '@cfp/features/authentication';
import { CustomerStyling } from '@cfp/features/customer-styling';
import { BUILDIG_LAYOUT } from '@cfp/shared/building/layout';
import { PORTFOLIO_LAYOUT } from '@cfp/shared/portfolio/layout';
import { LoadingSpinner } from '@cfp/ui/common';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  deLocale as deLocaleBootstrap,
  esLocale as esLocaleBootstrap,
  frLocale as frLocaleBootstrap,
  nlLocale as nlLocaleBootstrap,
  plLocale as plLocaleBootstrap,
  zhCnLocale as zhCnLocaleBootstrap
} from 'ngx-bootstrap/locale';
import { AppRouting } from '../../../../shared';
import { SharedAppRouting } from './app.routing';
import { SharedEnvironmentInitService, WindowLocationService } from './services';

const appEnvironmentFactory = (sharedEnvironmentInitService: SharedEnvironmentInitService): Environment => {
  return sharedEnvironmentInitService.environment as Environment;
};

function getEnvironmentCode(): string {
  const baseHref = document.getElementsByTagName('base')[0].attributes['href'].value;
  const baseHrefParts = baseHref.split('/').filter((part: string) => part);
  if (baseHrefParts.length === 3) {
    return baseHrefParts.pop();
  }

  const parts = window.location.pathname.split('/csr/app').pop().split('/');
  parts.shift();
  return parts.shift();
}

const baseHrefFactory = (): string => {
  const baseHref: string = getEnvironmentCode();
  if (!baseHref) {
    return '/csr/app/';
  }
  return `/csr/app/${getEnvironmentCode()}/`;
};

const appAvailableLanguagesFactory = (sharedEnvironmentInitService: SharedEnvironmentInitService): string[] => {
  return sharedEnvironmentInitService.environment?.availableLanguages;
};

const appCustomModulesFactory = (sharedEnvironmentInitService: SharedEnvironmentInitService): string[] => {
  return sharedEnvironmentInitService.environment?.translations;
};

function initEnvironment(sharedEnvironmentInitService: SharedEnvironmentInitService): () => Promise<any> {
  return (): Promise<any> => {
    const environmentCode = getEnvironmentCode();
    return sharedEnvironmentInitService.init(environmentCode);
  };
}

@NgModule({
  imports: [
    LoadingSpinner,
    RouterModule,
    CommonModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: ModuleTranslateLoaderFactory,
        deps: [
          HttpClient,
          APP_CUSTOM_MODULES,
          TranslateLoaderReadyService,
          TranslateCachedService,
          SharedEnvironmentInitService
        ]
      }
    }),
    SharedAppRouting,
    AppRouting,
    CoreModule,
    InterceptorsModule.forRoot(),
    AuthenticationModule,
    ModalModule,
    CustomerStyling
    /**
     * Never import customer modules here, this module is shared between different customers.
     * Use lazy loading by route or preloadModules in the environment.json
     * The lazy chunks will only be available for the specific customer when prefixed with environment code
     **/
  ],
  providers: [
    SharedEnvironmentInitService,
    WindowLocationService,
    TranslateCachedService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NabersApiUnavailableInterceptor,
      multi: true
    },
    {
      provide: APP_BASE_HREF,
      useFactory: baseHrefFactory
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initEnvironment,
      deps: [SharedEnvironmentInitService, APP_BASE_HREF],
      multi: true
    },
    {
      provide: ENVIRONMENT,
      useFactory: appEnvironmentFactory,
      deps: [SharedEnvironmentInitService]
    },
    {
      provide: APP_AVAILABLE_LANGUAGES,
      useFactory: appAvailableLanguagesFactory,
      deps: [SharedEnvironmentInitService]
    },
    {
      provide: PORTFOLIO_LAYOUT,
      useValue: PortfolioLayout
    },
    {
      provide: BUILDIG_LAYOUT,
      useValue: BuildingLayout
    },
    {
      provide: APP_LOCALE_DATA,
      useValue: {
        nl: {
          localeDataAngular: nlLocaleAngular,
          localeDataBootstrap: nlLocaleBootstrap
        },
        de: {
          localeDataAngular: deLocaleAngular,
          localeDataBootstrap: deLocaleBootstrap
        },
        fr: {
          localeDataAngular: frLocaleAngular,
          localeDataBootstrap: frLocaleBootstrap
        },
        pl: {
          localeDataAngular: plLocaleAngular,
          localeDataBootstrap: plLocaleBootstrap
        },
        el: {
          localeDataAngular: elLocaleAngular
        },
        es: {
          localeDataAngular: esLocaleAngular,
          localeDataBootstrap: esLocaleBootstrap
        },
        'es-MX': {
          localeDataAngular: esMxLocaleAngular,
          localeDataBootstrap: esLocaleBootstrap
        },
        'en-AU': {
          localeDataAngular: enAuLocaleAngular
        },
        'zh-Hans': {
          localeDataAngular: zhHansLocaleAngular,
          localeDataBootstrap: zhCnLocaleBootstrap
        },
        'zh-Hant': {
          localeDataAngular: zhHantLocaleAngular,
          localeDataBootstrap: zhCnLocaleBootstrap
        }
      }
    },
    {
      provide: APP_CUSTOM_MODULES,
      useFactory: appCustomModulesFactory,
      deps: [SharedEnvironmentInitService]
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
